import { createTranslateFactory, type Translate } from "@universe/i18n";
import en from "./translations/en.json";
import {
  AVAILABLE_LOCALES,
  DEFAULT_LOCALE,
  resolveLocaleOrDefault,
  type SiteLocale,
} from "./locales";

export type { Translate } from "@universe/i18n";
export * from "./locales";

type TranslationDictionary = Record<string, string>;

const loadedTranslations: Partial<Record<SiteLocale, TranslationDictionary>> = {
  en,
};

const inFlightLocaleLoads: Partial<Record<SiteLocale, Promise<TranslationDictionary>>> = {};

const translationLoaders: Record<SiteLocale, () => Promise<TranslationDictionary>> = {
  en: async () => en,
  id: async () => (await import("./translations/id.json")).default,
  de: async () => (await import("./translations/de.json")).default,
  es: async () => (await import("./translations/es.json")).default,
  fr: async () => (await import("./translations/fr.json")).default,
  it: async () => (await import("./translations/it.json")).default,
  nl: async () => (await import("./translations/nl.json")).default,
  pl: async () => (await import("./translations/pl.json")).default,
  "pt-BR": async () => (await import("./translations/pt-BR.json")).default,
  fi: async () => (await import("./translations/fi.json")).default,
  sv: async () => (await import("./translations/sv.json")).default,
  vi: async () => (await import("./translations/vi.json")).default,
  tr: async () => (await import("./translations/tr.json")).default,
  ru: async () => (await import("./translations/ru.json")).default,
  ar: async () => (await import("./translations/ar.json")).default,
  hi: async () => (await import("./translations/hi.json")).default,
  th: async () => (await import("./translations/th.json")).default,
  ko: async () => (await import("./translations/ko.json")).default,
  ja: async () => (await import("./translations/ja.json")).default,
  "zh-CN": async () => (await import("./translations/zh-CN.json")).default,
};

function getLoadedTranslations(): Record<string, TranslationDictionary> {
  return Object.fromEntries(
    Object.entries(loadedTranslations).filter(([, dictionary]) => dictionary)
  ) as Record<string, TranslationDictionary>;
}

/**
 * Load locale translations on demand.
 * Returns true if a new locale dictionary was loaded, false if it was already available.
 */
export async function ensureLocaleTranslations(locale?: string): Promise<boolean> {
  const resolvedLocale = resolveLocaleOrDefault(locale);
  if (loadedTranslations[resolvedLocale]) {
    return false;
  }

  const existingLoad = inFlightLocaleLoads[resolvedLocale];
  if (existingLoad) {
    await existingLoad;
    return false;
  }

  const loadPromise = translationLoaders[resolvedLocale]().then((dictionary) => {
    loadedTranslations[resolvedLocale] = dictionary;
    return dictionary;
  });

  inFlightLocaleLoads[resolvedLocale] = loadPromise;

  try {
    await loadPromise;
    return true;
  } finally {
    delete inFlightLocaleLoads[resolvedLocale];
  }
}

/**
 * Create a Translate instance for a given locale code.
 * Falls back to English if the locale is not available.
 */
export function createTranslate(locale?: string): Translate {
  const createSiteTranslate = createTranslateFactory({
    translations: getLoadedTranslations(),
    defaultLocale: DEFAULT_LOCALE,
  });
  return createSiteTranslate(locale);
}

/**
 * Default Translate instance (English)
 */
export const defaultTranslate: Translate = createTranslate(DEFAULT_LOCALE);

/**
 * Get available locales
 */
export function getAvailableLocales(): SiteLocale[] {
  return [...AVAILABLE_LOCALES];
}
